// Wire shapes mirror the Go structs in cmd/console-api. Keep these in
// sync with the handler responses — the SPA depends on field names and
// optional/required exactly as the server emits them.

// REGIONS is the single source of truth for the serving regions: the
// `Region` union below is DERIVED from it, and GlowCreate's radio options
// are rendered from the same array — so adding a region is a one-line push
// that both the type and the picker follow (no union↔radio drift). Order is
// the display order in the picker (US default, then EU, then AU).
export const REGIONS = [
  { value: 'us-central1', label: 'US Central (Iowa)', description: 'us-central1 — lowest latency from North America.' },
  { value: 'europe-west1', label: 'Europe (Belgium)', description: 'europe-west1 — EU residency or audience.' },
  { value: 'australia-southeast1', label: 'Australia (Sydney)', description: 'australia-southeast1 — for APAC residency or audience.' },
] as const
export type Region = (typeof REGIONS)[number]['value']
export type AuthMode = 'Public' | 'Private' | 'Accounts'
export type LitStatus = 'Lit' | 'Dark'

// BillingInterval is the Stripe-verbatim billing interval — the wire spelling
// everywhere (CLAUDE.md §11). "Monthly"/"Yearly" labels are presentation only.
export type BillingInterval = 'month' | 'year'

// KnownPlan is the canonical base-tier union — the plans the picker can
// switch/check out TO. Subscription.plan is deliberately WIDER (string): a
// custom subscriber carries their price's backlit_plan slug verbatim.
export type KnownPlan = 'spark' | 'beam' | 'flood'

export interface Subscription {
  status: 'none' | 'active' | 'trialing' | 'past_due' | 'canceled' | 'incomplete'
  can_set_public: boolean
  can_add_emails: boolean
  can_add_patterns: boolean
  can_choose_name: boolean
  can_get_cname: boolean
  // Open self-serve Accounts auth mode (Flood+).
  can_set_accounts: boolean
  // Remove the SDK-injected Backlit FAB (any paid plan).
  can_hide_branding: boolean
  // True once a Stripe Customer record exists for the owner. Does NOT imply an
  // active subscription — free Spark owners are mirrored into Stripe as a
  // Customer at glow-create. Choose Subscribe vs Manage from `status`, not this.
  has_billing_customer: boolean
  // Maximum number of glows the owner may create. Free owners get
  // FREE_TIER_MAX_GLOWS; active-paid owners get the configured price
  // value. 0 means unlimited (mirrors the UsageTab cap convention).
  max_glows: number
  // The highest base tier the owner's subscription resolves to. Comes
  // straight from the server (NEVER inferred from the can_* booleans) —
  // 'spark' is the free tier, 'beam'/'flood' are the paid base tiers. A
  // custom subscriber (is_custom) carries their price's backlit_plan slug
  // verbatim, so this is a string, not the KnownPlan union.
  plan: string
  // The subscription's billing interval, Stripe-verbatim ("month"/"year");
  // "" for a free/none owner. Seeds the Billing page's monthly/yearly toggle
  // and is the interval the Amp section follows.
  interval: BillingInterval | ''
  // The base price's backlit_label display label ("" when unset/free).
  // Titles the custom-plan Billing panel (plan_label || 'Custom Plan').
  plan_label: string
  // True when the subscriber's base price is not among the catalog's card
  // prices — the Billing page renders a 4th custom-plan panel from the
  // resolved caps below instead of highlighting a picker card.
  is_custom: boolean
  // True when the owner has scheduled a downgrade to Spark: paid features stay
  // until the current period ends, then the sub drops to free. The Billing page
  // shows a banner + a Resume action while this is set.
  cancel_at_period_end: boolean
  // The resolved account-wide caps the data plane enforces (denormalized
  // onto account_usage/{owner_uid}). 0 means unlimited.
  storage_cap_bytes: number
  egress_cap_bytes: number
  requests_cap: number
}

export interface Me {
  uid: string
  email: string
  display_name: string
  subscription: Subscription
}

// PlanQuotas / PlanPrice / BillingPlan / AmpPack / BillingPlansResponse mirror
// the catalog returned by GET /v1/billing/plans — the single source for plan
// metadata and Stripe price ids in the SPA (D8: no VITE_STRIPE_PRICE_*).
// Plans are ordered spark/beam/flood; amp_packs ascending by storage. A
// `price_id` may be "" when Stripe isn't configured yet — guard before
// offering a checkout action.
export interface PlanQuotas {
  storage_bytes: number
  egress_bytes: number
  requests: number
  max_glows: number
  retention: string
}

// PlanPrice is one purchasable interval of a plan card / Amp pack: the Stripe
// price id, the server-derived display label, and the native amount/currency.
// An empty price_id means "not purchasable" (Spark, or the no-Stripe fallback).
export interface PlanPrice {
  price_id: string
  price_label: string
  unit_amount: number
  currency: string
}

// PlanPrices is keyed by the Stripe-verbatim interval. A configured plan
// omits an unconfigured interval's key entirely; Spark and the fallback
// picker carry both keys (with empty price ids where nothing is purchasable).
export interface PlanPrices {
  month?: PlanPrice
  year?: PlanPrice
}

export interface BillingPlan {
  plan: KnownPlan
  display_name: string
  prices: PlanPrices
  // Quotas are interval-invariant (the same caps whichever interval is billed).
  quotas: PlanQuotas
  features: string[]
}

export interface AmpPack {
  display_name: string
  add_storage_bytes: number
  prices: PlanPrices
}

export interface BillingPlansResponse {
  plans: BillingPlan[]
  amp_packs: AmpPack[]
}

// AmpItem / AmpState mirror GET + POST /v1/me/billing/amp — the Amp storage
// packs currently attached to the owner's active subscription, with quantity.
// Amp packs are additive subscription line items the Stripe Customer Portal
// can't attach, so the console adds/removes them via these endpoints; the
// account caps re-resolve asynchronously via the Stripe webhook.
export interface AmpItem {
  price_id: string
  quantity: number
  add_storage_bytes: number
  // The pack's Stripe-verbatim billing interval — always the subscription's
  // (Stripe forbids mixing intervals on one subscription).
  interval: string
}

export interface AmpState {
  items: AmpItem[]
}

// Permission is the access level of an allowed user / pattern, stored as a
// lowercase enum (the wire contract). The owner is always 'admin'.
// 'viewer' users cannot write shared data via the SDK
// (window.backlit.data.put/delete). Human-readable labels (Admin / User /
// Viewer) live only in the UI — see PERMISSION_LABELS in permissions.tsx.
export type Permission = 'admin' | 'user' | 'viewer'

// GlowUser mirrors the rows returned by GET /v1/glows/{id}/users (the
// granular user-management surface that replaced the old allowlist array).
// `uid` is the stable server-allocated identifier that keys the user's
// per-user data silo and is the path segment for PATCH/DELETE. `source`
// tells the UI where the row came from:
//   - owner   → the glow owner (always admin, never deletable);
//   - manual  → an explicitly-added allowed_users entry;
//   - pattern → a materialized allowed_patterns match. Editing one server-side
//     pins it (pattern → manual) — the SPA just PATCHes by uid.
export interface GlowUser {
  uid: string
  email: string
  name: string
  mobile: string
  // The server-normalized E.164 form of `mobile` (the SMS-login identifier).
  // OPTIONAL on the wire: a current server emits it ("" when no valid mobile),
  // but an older/not-yet-deployed console-api omits the key entirely, so it can
  // be undefined at runtime — typed optional so tsc forces that handling and the
  // table never crashes against an older backend (the `category?` precedent).
  // The "can SMS-login" signal is derived from `mobile` being non-empty, not
  // from this field. Display still uses `mobile` as entered.
  mobile_e164?: string
  permission: Permission
  source: 'owner' | 'manual' | 'pattern'
  created_at?: string
  updated_at?: string
}

// GlowUsersResponse is the envelope of GET /v1/glows/{id}/users. The roster is
// cursor-paginated (CP3 — a broad-domain allowlist can materialize many pattern
// users): `next` is the opaque cursor for the following page, "" / absent once
// the roster is exhausted.
export interface GlowUsersResponse {
  users: GlowUser[]
  next?: string
}

// AddUserRequest is the body of POST /v1/glows/{id}/users.
export interface AddUserRequest {
  email: string
  name?: string
  mobile?: string
  permission: Permission
}

// UpdateUserRequest is the body of PATCH /v1/glows/{id}/users/{uid}.
// All fields optional — only the ones present are changed.
export interface UpdateUserRequest {
  name?: string
  mobile?: string
  permission?: Permission
}

// UpdatePatternsRequest is the body of PUT /v1/glows/{id}/patterns
// (full-replace of the domain-pattern list).
export interface UpdatePatternsRequest {
  allowed_patterns: AllowedPattern[]
}

// AllowedPattern mirrors firestore.AllowedPattern: a domain pattern like
// "@company.com" plus the permission granted to users it admits.
export interface AllowedPattern {
  pattern: string
  permission: Permission
}

export interface Glow {
  id: string
  name: string
  // Free-text display label (max 100 chars), surfaced in the UI as the
  // glow's heading. Distinct from `name`, the subdomain label (shown as
  // "Beacon"). Empty string when no title is set.
  title: string
  // Free-text grouping label for the "Your glows" list (max 50 chars).
  // Distinct from title/name; free, NOT subscription-gated. OPTIONAL on the
  // wire: a current server emits "" when unset, but an older/not-yet-deployed
  // console-api omits the key entirely, so the value can be undefined at
  // runtime. Consumers MUST treat undefined/"" the same: coalesce
  // `glow.category || ""` for grouping (→ "Uncategorized") and filter undefined
  // out of suggestion lists. Typed optional so tsc forces that handling.
  category?: string
  region: Region
  auth_mode: AuthMode
  // When true (and the owner is on a paid plan), suppresses the SDK-injected
  // Backlit FAB on this glow. The per-glow half of the paid branding opt-out.
  hide_branding: boolean
  allowed_patterns: AllowedPattern[]
  status: LitStatus
  // Why a Lit glow went Dark, or "" for an owner-chosen Dark / a Lit glow.
  // Currently only "downgrade" — the glow was auto-darkened because the owner
  // dropped below the plan's glow limit. Always present (server emits "" when
  // unset); compare === 'downgrade' explicitly, never truthiness, so a future
  // reason value can't accidentally match.
  dark_reason: string
  owner_uid: string
  live_version: string
  draft_version: string
  created_at: string
  updated_at: string
}

export interface APIKey {
  id: string
  glow_id: string
  created_at: string
}

// APIKeyWithToken is the response shape of POST .../api-keys — the only
// place the plaintext token is ever returned. Subsequent reads of the
// same key (list) never include `token`. Reveal-once.
export interface APIKeyWithToken extends APIKey {
  token: string
}

export interface Version {
  id: string
  created_at: string
  size: number
  is_live: boolean
  is_draft: boolean
  // Whether this version's deploy bundle carried a valid root offline.json.
  // The server always emits it; optional here only so this hand-mirrored type
  // still decodes responses from before the field existed.
  offline_enabled?: boolean
  // Exactly one of these is set, depending on which mint path issued the
  // deploy: an OAuth-authenticated user (console / MCP) fills
  // uploader_user_uid; a per-glow API key fills uploader_key_hash.
  uploader_user_uid?: string
  uploader_key_hash?: string
}

export interface DeployTicketResponse {
  upload_url: string
  expires_at: string
}

export interface PromoteDraftResponse {
  live_version: string
  draft_version: string
  // Version ids of the intermediate Drafts swept by this promote (those staged
  // between the old live version and the promoted one). Empty when nothing was
  // reclaimed.
  deleted_version_ids: string[]
}

export interface BillingURL {
  url: string
}

export interface CreateGlowRequest {
  name?: string
  title?: string
  // Free-text grouping label (max 50 chars); omit or "" leaves it unset
  // (Uncategorized). Free, NOT subscription-gated — mirrors `title`.
  category?: string
  region: Region
  auth_mode?: AuthMode
  hide_branding?: boolean
  status?: LitStatus
}

export interface UpdateGlowRequest {
  name?: string
  title?: string
  // Free-text grouping label (max 50 chars). Send "" to clear it
  // (move-to-Uncategorized), exactly like `title`. Free, NOT subscription-gated.
  category?: string
  auth_mode?: AuthMode
  hide_branding?: boolean
  status?: LitStatus
}

// CheckoutSessionRequest starts a Stripe Checkout for a paid base tier at a
// billing interval — the server resolves (plan, interval) to a price via its
// catalog. Raw price ids are not accepted; spark is free (nothing to check
// out), so it is excluded structurally.
export interface CheckoutSessionRequest {
  plan: Exclude<KnownPlan, 'spark'>
  interval: BillingInterval
  success_url: string
  cancel_url: string
}

export interface PortalSessionRequest {
  return_url: string
}

// AmpChangeRequest names an Amp pack + a desired absolute quantity (0 removes
// it). Used both to preview the prorated cost and to apply the change.
export interface AmpChangeRequest {
  price_id: string
  quantity: number
}

// AmpPreview is the body of POST /v1/me/billing/amp/preview — the exact amount
// the change would invoice now (in the currency's minor units; positive =
// charge, negative = credit), plus the proration_date the figure was computed
// at. The SPA passes proration_date back to the apply call so the charge matches.
export interface AmpPreview {
  amount_due: number
  currency: string
  proration_date: number
}

// UpdateAmpRequest applies an Amp change. proration_date carries the value from
// a prior preview so the immediate charge equals the quoted figure.
export interface UpdateAmpRequest {
  price_id: string
  quantity: number
  proration_date?: number
}

// ChangePlanRequest switches an active subscriber's base plan in-app. plan is
// the target tier ('beam'/'flood' swap the price, 'spark' schedules a downgrade
// at period end — you can never switch TO a custom plan); interval is the
// target billing interval (ignored for spark; the server decides noop by
// price-id equality, so a same-plan interval change is a real switch).
// proration_date rides from a prior preview.
export interface ChangePlanRequest {
  plan: KnownPlan
  interval: BillingInterval
  proration_date?: number
}

// PlanPreview is POST /v1/me/billing/plan/preview's body: the action the change
// performs ('switch' = prorated price/interval swap, 'cancel' = downgrade to
// Spark at period end, 'noop' = already on that exact price) and, for a switch,
// the exact amount it bills now (minor units; positive = charge, negative =
// credit) + proration_date. On a same-interval switch amount_due is the
// prorated delta; on an INTERVAL change it is the TOTAL immediate invoice
// (proration credits + the full first new-interval period — the billing cycle
// anchor resets, so the dialog says the new billing period starts today).
export interface PlanPreview {
  action: 'switch' | 'cancel' | 'noop'
  amount_due: number
  currency: string
  proration_date: number
}

export interface RollbackRequest {
  version_id: string
}

// DataVersion mirrors the JSON returned by GET /v1/glows/{id}/data-history.
// Phase 11.6: per-key version history for the shared `data.*` surface.
export interface DataVersion {
  generation: number
  time_created: string
  size: number
  content_type: string
  md5?: string
}

export interface DataHistoryResponse {
  versions: DataVersion[]
  next_cursor?: string
}

export interface DataRestoreRequest {
  key: string
  generation: number
}

export interface DataRestoreResponse {
  restored_generation: number
  size: number
  content_type?: string
}

// DataKey is one row in the directory-style file listing returned by
// GET /v1/glows/{id}/data-keys. `path` is the SDK-visible key (no
// "shared/" prefix); `generation`, `time_created`, and `md5` describe
// which underlying GCS generation this row represents — useful for
// rendering "Restore" actions that target an exact prior version.
export interface DataKey {
  path: string
  size: number
  content_type: string
  generation: number
  time_created: string
  md5?: string
}

export interface DataKeysResponse {
  keys: DataKey[]
  total_size: number
}

// GlowUsage mirrors the console-api usageResponse (cmd/console-api/usage.go)
// served by GET /v1/glows/{id}/usage — the single data source for the Usage
// tab. `storage` is the live GCS footprint (the five cap-charged groups);
// `bandwidth` is the monthly counters. As of account-wide limits the cap
// fields (`storage.cap_bytes`, `bandwidth.*_cap`) are the **account-wide**
// caps, and the new `account_*` fields carry the account-summed counters that
// actually drive enforcement (the meter that matters — risk X5). A zero cap
// means unlimited — now reserved for a genuinely-unlimited (future paid) cap:
// un-resolved/free owners receive concrete Spark caps (the backend floors them
// via ResolveCaps), so these fields are normally non-zero.
//
// The `account_*` fields are typed **optional**: Slice 2 returns them, but the
// data plane / console-api deploy independently, so a slightly older console-api
// may omit them. The UI guards them with `=== undefined` (never `|| fallback`,
// since 0 is a valid counter) and degrades like the `breakdown_available:false`
// path when they are absent.
//
// When `breakdown_available` is false the server emits `groups: {}`; the five
// keys are still typed explicitly (the UI guards on `breakdown_available`
// before reading them).
export interface GlowUsage {
  storage: {
    cap_bytes: number // account-wide storage cap (0 = unlimited; free owners get concrete Spark caps)
    total_bytes: number // this glow's live GCS footprint
    account_total_bytes?: number // account-summed storage (data_bytes_used)
    groups: {
      bundles: number
      shared: number
      users: number
      private: number
      records: number
    }
    truncated: boolean
    breakdown_available: boolean
  }
  bandwidth: {
    egress_bytes_used: number // this glow's monthly egress
    egress_cap_bytes: number // account-wide egress cap (0 = unlimited; free owners get concrete Spark caps)
    account_egress_bytes_used?: number // account-summed monthly egress
    requests_count: number // this glow's monthly requests
    requests_cap: number // account-wide request cap (0 = unlimited; free owners get concrete Spark caps)
    account_requests_count?: number // account-summed monthly requests
    last_usage_reset_at: string // RFC3339
  }
}

// AccountUsage mirrors the console-api meUsageResponse served by
// GET /v1/me/usage — the dedicated account-wide aggregate backing the /usage
// page. Counters are account-summed (across every glow the owner controls) and
// the caps are the tier-resolved account caps (resolved via ResolveCaps); a zero
// cap means unlimited, now reserved for a genuinely-unlimited (future paid) cap.
// A brand-new owner with no account doc returns all-zero COUNTERS but concrete
// SPARK caps at HTTP 200 (not 404), so this is always safe to render and the
// meters fill rather than showing "Unlimited".
export interface AccountUsage {
  storage: {
    bytes_used: number
    cap_bytes: number
  }
  bandwidth: {
    egress_bytes_used: number
    egress_cap_bytes: number
    requests_count: number
    requests_cap: number
    last_usage_reset_at: string // RFC3339
  }
}

// DataSnapshotDelta is the +N ~N -N counter the VersionPicker renders
// next to each menu row. Each field is the count of paths in that
// category at this snapshot vs the snapshot one bucket older.
export interface DataSnapshotDelta {
  added: number
  modified: number
  removed: number
}

// DataSnapshot is one entry in the VersionPicker dropdown. `bucket_at`
// is the per-minute floored timestamp the SPA passes back as `at=`
// when the user picks this snapshot; `generation_high` is the largest
// generation that landed in this minute (a stable id to pin selection
// against).
export interface DataSnapshot {
  bucket_at: string
  generation_high: number
  delta: DataSnapshotDelta
}

export interface DataSnapshotsResponse {
  snapshots: DataSnapshot[]
  next_cursor?: string
}
